import type { AudioUploadMeta } from './api';
import { FILE_MARKERS } from '@shared/voiceMeta';

// DB-gestuetzte Vokabular-Namenslisten (Etappe 5.5) — vom Aufrufer aus
// useVocabulary() uebergeben (Kategorie/Dialekt/Akzent). Sprachen bleiben Const.
export interface PredictVocab {
  dialects: readonly string[];
  accents: readonly string[];
  categories: readonly string[];
}

// Invertierte FILE_MARKERS: markerLower → Sprachcode (null-Marker wie 'de' raus).
const MARKER_MAP = new Map(
  Object.entries(FILE_MARKERS)
    .filter((e): e is [string, string] => Boolean(e[1]))
    .map(([code, marker]) => [marker.toLowerCase(), code] as const),
);

// Lowercase-Lookup → Originalwert (exakte Token-Gleichheit).
const lowerMap = (names: readonly string[]) => new Map(names.map((n) => [n.toLowerCase(), n] as const));

// Kehrt das Naming-Schema (worker/lib/naming.js buildAudioFilename) um: erkennt aus
// dem lokalen Dateinamen Sprachmarker / Dialekt / Akzent / Kategorie / DemoMix.
export function predictAudioMeta(filename: string, vocab: PredictVocab): Partial<AudioUploadMeta> {
  const categoryMap = lowerMap(vocab.categories);
  const dialectMap = lowerMap(vocab.dialects);
  const accentMap = lowerMap(vocab.accents);

  const base = filename.replace(/\.mp3$/i, '');
  // Marker-Tokens: NICHT am Bindestrich zerlegen (de-CH / en-US bleiben ganz).
  const tokens = base.split(/[_\s]+/).filter(Boolean);
  // Vokabular-Tokens: zusätzlich am Bindestrich zerlegt (bindestrich-getrennte Namen).
  const subTokens = tokens.flatMap((t) => t.split('-')).filter(Boolean);
  const result: Partial<AudioUploadMeta> = {};

  for (const t of tokens) {
    const code = MARKER_MAP.get(t.toLowerCase());
    if (code && result.language === undefined) result.language = code;
  }
  for (const t of subTokens) {
    const low = t.toLowerCase();
    if (result.language === undefined && MARKER_MAP.has(low)) result.language = MARKER_MAP.get(low);
    if (result.category === undefined && categoryMap.has(low)) result.category = categoryMap.get(low);
    if (result.dialect === undefined && dialectMap.has(low)) result.dialect = dialectMap.get(low);
    if (result.accent === undefined && accentMap.has(low)) result.accent = accentMap.get(low);
    if (result.is_demomix === undefined && low === 'demomix') result.is_demomix = true;
  }
  return result;
}
